import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/ProtectedRoute';
import ROLE from 'assets/userRoles.json';
import { RootState } from 'redux/store';
import InitialDocuments from './InitialDocuments';

/**
 * Menus inside the EXTERNAL_CLIENT visa-process flow where the Initial
 * Documents card should appear. The client can upload Passport / Resume /
 * Proof of Address from the assessment form and from the Documents, Chats and
 * Agreement screens. The Visa Offers and Results screens are intentionally
 * excluded.
 *
 * Matching is done on the lowercased pathname so the capitalised `Agreement`
 * route segment (`visa-process/:id/Agreement`) still matches.
 */
const ALLOWED_MENU_SEGMENTS = ['/assessment', '/documents', '/chats', '/agreement'];

/**
 * Renders the Initial Documents card for the EXTERNAL_CLIENT (visa-process)
 * flow. The external client *is* the candidate, so the card is scoped to the
 * logged-in user's own id (`state.user.entity.id`) rather than a URL param —
 * note the `:id` in `visa-process/:id/...` is the application id, not the
 * candidate id, so it must not be used here.
 *
 * Renders nothing unless the user is an external client currently on one of
 * the allowed menus, keeping the card out of the Visa Offers / Results
 * screens and away from every other role.
 */
const ClientFlowInitialDocuments = () => {
    const location = useLocation();
    const { role } = useAuth();
    const userId = useSelector((state: RootState) => state.user.entity.id);

    if (role !== ROLE.externalClient || !userId) {
        return null;
    }

    const lowerPath = location.pathname.toLowerCase();
    const onAllowedMenu = ALLOWED_MENU_SEGMENTS.some((segment) => lowerPath.includes(segment));

    if (!onAllowedMenu) {
        return null;
    }

    return <InitialDocuments userId={userId} />;
};

export default ClientFlowInitialDocuments;
